import { Component, Input, Output, EventEmitter, ViewEncapsulation, HostListener, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

export interface DateRange {
    start: Date | null;
    end: Date | null;
}

@Component({
    selector: 'app-date-range-picker',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule
    ],
    templateUrl: './date-range-picker.html',
    styleUrls: ['./date-range-picker.scss'],
    encapsulation: ViewEncapsulation.None
})
export class DateRangePickerComponent {
    @Input() placeholder: string = 'Select Date Range';
    @Output() dateRangeChange = new EventEmitter<DateRange>();

    isPickerOpen = false;
    monthDropdownOpen = false;
    yearDropdownOpen = false;

    // Calendar State
    currentDate = new Date();
    currentMonth = this.currentDate.getMonth();
    currentYear = this.currentDate.getFullYear();

    // Selection State
    startDate: Date | null = null;
    endDate: Date | null = null;

    // Data for View
    weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];
    years: number[] = [];
    calendarDays: any[] = [];

    constructor(private elementRef: ElementRef) {
        this.generateYears();
        this.generateCalendar();
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        const clickedInside = this.elementRef.nativeElement.contains(event.target);
        if (!clickedInside && this.isPickerOpen) {
            this.closePicker();
        }
        this.monthDropdownOpen = false;
        this.yearDropdownOpen = false;
    }

    generateYears() {
        const currentYear = new Date().getFullYear();
        for (let i = currentYear - 10; i <= currentYear + 10; i++) {
            this.years.push(i);
        }
    }

    generateCalendar() {
        this.calendarDays = [];
        const firstDayOfMonth = new Date(this.currentYear, this.currentMonth, 1);
        const lastDayOfMonth = new Date(this.currentYear, this.currentMonth + 1, 0);

        // Adjust for Monday start (0 = Sunday, 1 = Monday, ...)
        let startDay = firstDayOfMonth.getDay() - 1;
        if (startDay === -1) startDay = 6; // Sunday becomes 6

        const daysInMonth = lastDayOfMonth.getDate();

        // Previous month days
        const prevMonthLastDay = new Date(this.currentYear, this.currentMonth, 0).getDate();
        for (let i = startDay - 1; i >= 0; i--) {
            this.calendarDays.push({
                date: new Date(this.currentYear, this.currentMonth - 1, prevMonthLastDay - i),
                isCurrentMonth: false,
                day: prevMonthLastDay - i
            });
        }

        // Current month days
        for (let i = 1; i <= daysInMonth; i++) {
            this.calendarDays.push({
                date: new Date(this.currentYear, this.currentMonth, i),
                isCurrentMonth: true,
                day: i
            });
        }

        // Next month days to complete grid (42 cells for 6 rows)
        const remainingCells = 42 - this.calendarDays.length;
        for (let i = 1; i <= remainingCells; i++) {
            this.calendarDays.push({
                date: new Date(this.currentYear, this.currentMonth + 1, i),
                isCurrentMonth: false,
                day: i
            });
        }
    }

    selectDate(date: Date) {
        // Prevent selection of days not in current month
        if (date.getMonth() !== this.currentMonth) {
            return;
        }

        // Reset time to midnight for comparison
        const selectedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

        if (!this.startDate || (this.startDate && this.endDate)) {
            // Start new selection
            this.startDate = selectedDate;
            this.endDate = null;
        } else if (this.startDate && !this.endDate) {
            if (selectedDate < this.startDate) {
                this.startDate = selectedDate;
            } else {
                this.endDate = selectedDate;
            }
        }
    }

    isDateSelected(date: Date): boolean {
        if (!this.startDate) return false;
        return date.getTime() === this.startDate.getTime() ||
            (this.endDate ? date.getTime() === this.endDate.getTime() : false);
    }

    isDateInRange(date: Date): boolean {
        if (!this.startDate || !this.endDate) return false;
        return date > this.startDate && date < this.endDate;
    }

    isStartDate(date: Date): boolean {
        return this.startDate ? date.getTime() === this.startDate.getTime() : false;
    }

    isEndDate(date: Date): boolean {
        return this.endDate ? date.getTime() === this.endDate.getTime() : false;
    }

    toggleMonthDropdown(event: Event) {
        event.stopPropagation();
        this.monthDropdownOpen = !this.monthDropdownOpen;
        if (this.monthDropdownOpen) {
            this.yearDropdownOpen = false;
        }
    }

    toggleYearDropdown(event: Event) {
        event.stopPropagation();
        this.yearDropdownOpen = !this.yearDropdownOpen;
        if (this.yearDropdownOpen) {
            this.monthDropdownOpen = false;
        }
    }

    selectMonth(month: number) {
        this.currentMonth = month;
        this.monthDropdownOpen = false;
        this.generateCalendar();
    }

    selectYear(year: number) {
        this.currentYear = year;
        this.yearDropdownOpen = false;
        this.generateCalendar();
    }

    onMonthChange(event: any) {
        this.currentMonth = parseInt(event.target.value);
        this.generateCalendar();
    }

    onYearChange(event: any) {
        this.currentYear = parseInt(event.target.value);
        this.generateCalendar();
    }

    getFormattedDateRange(): string {
        if (this.startDate && this.endDate) {
            const formatDate = (date: Date) => {
                const day = date.getDate();
                const month = date.toLocaleDateString('en-US', { month: 'short' });
                const year = date.getFullYear();
                return `${day} ${month} ${year}`;
            };
            return `${formatDate(this.startDate)} - ${formatDate(this.endDate)}`;
        }
        return this.placeholder;
    }

    togglePicker() {
        this.isPickerOpen = !this.isPickerOpen;
    }

    closePicker() {
        this.isPickerOpen = false;
    }

    applyDateRange() {
        if (this.startDate && this.endDate) {
            this.dateRangeChange.emit({
                start: this.startDate,
                end: this.endDate
            });
            this.closePicker();
        }
    }

    reset() {
        this.startDate = null;
        this.endDate = null;
        this.currentDate = new Date();
        this.currentMonth = this.currentDate.getMonth();
        this.currentYear = this.currentDate.getFullYear();
        this.generateCalendar();
    }
}
